import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Box } from '@chakra-ui/core';
import { camelCase } from 'camel-case';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { BaseTabs, TabList, Tab, TabWithBadge, TabPanels } from '../common/components/Tabs';
import routes, { getTasksPath } from '../common/routes';
import { history } from '../common/helpers/history';
import { PageTitle } from '../common/components/Titles';
import { AllTasksTab } from './AllTasksTab';
import { FilteredTasksTab } from './FilteredTasksTab';
import { TabPanel, TASKS_TYPES } from './components';
import { tasksActions } from '../redux/tasks/actions';
import { useOperatorFilter } from '../common/contexts/OperatorFilterContext';
import { Pagination } from '../common/components/Pagination';

const TASKS_PER_PAGE = 25;
const ALL_TASKS_FILTER_KEY = 'all';
const EMPTY_FILTERS = { tags: [], operators: [], details: [] };

const toArray = value => (Array.isArray(value) ? value : [value].filter(Boolean));

const TasksTabs = ({
  match: {
    params: { tasksType },
  },
  tasks,
  meta,
  pending,
  getTasks,
}) => {
  const { isFilterDisabled } = useOperatorFilter();
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersByTaskType, setFiltersByTaskType] = useState({});

  const tabIndex = routes.tasks.types.indexOf(`/${tasksType}`);
  const selectedTaskType = tabIndex >= 0 ? Object.values(TASKS_TYPES)[tabIndex] : undefined;
  const filterKey = selectedTaskType || ALL_TASKS_FILTER_KEY;
  const filters = filtersByTaskType[filterKey] || EMPTY_FILTERS;
  const selectedPanelIndex = tabIndex >= 0 ? tabIndex + 1 : 0;

  const requestOptions = useMemo(
    () => ({
      isFilterDisabled,
      perPage: TASKS_PER_PAGE,
      taskType: selectedTaskType,
      ...filters,
    }),
    [filters, isFilterDisabled, selectedTaskType],
  );
  const requestScopeKey = JSON.stringify(requestOptions);
  const previousRequestScopeKey = useRef(requestScopeKey);
  const inFlightAbortController = useRef(null);

  const fetchTasks = useCallback(
    (page = currentPage) => {
      if (inFlightAbortController.current) {
        inFlightAbortController.current.abort();
      }
      const controller = new AbortController();
      inFlightAbortController.current = controller;
      return getTasks({
        ...requestOptions,
        page,
        signal: controller.signal,
      });
    },
    [currentPage, getTasks, requestOptions],
  );

  useEffect(() => {
    const requestScopeChanged = previousRequestScopeKey.current !== requestScopeKey;
    previousRequestScopeKey.current = requestScopeKey;

    if (requestScopeChanged && currentPage !== 1) {
      setCurrentPage(1);
      return undefined;
    }

    fetchTasks();
    return () => {
      if (inFlightAbortController.current) {
        inFlightAbortController.current.abort();
        inFlightAbortController.current = null;
      }
    };
  }, [currentPage, fetchTasks, requestScopeKey]);

  const lastPage = meta?.totalPages;
  useEffect(() => {
    if (!pending && lastPage > 0 && currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, lastPage, pending]);

  if (!tasks) return null;

  const tags = meta?.filterOptions?.tags || [];
  const operators = meta?.filterOptions?.operators || [];
  const details = meta?.filterOptions?.details || [];
  const countsByType = meta?.countsByType || {};
  const taskTypeCount = label => countsByType[label] || countsByType[camelCase(label)] || 0;

  const updateFilter = (filterName, value) => {
    setCurrentPage(1);
    setFiltersByTaskType(currentFiltersByTaskType => ({
      ...currentFiltersByTaskType,
      [filterKey]: {
        ...(currentFiltersByTaskType[filterKey] || EMPTY_FILTERS),
        [filterName]: toArray(value),
      },
    }));
  };

  const clearPageAndPush = index => {
    setCurrentPage(1);
    history.push(index ? getTasksPath(index - 1) : routes.tasks.index);
  };

  const paginationTotal = Math.min(
    meta?.totalCount || 0,
    (meta?.totalPages || 0) * (meta?.perPage || TASKS_PER_PAGE),
  );

  const pagination = meta && paginationTotal > meta.perPage && (
    <Box mt={4}>
      <Pagination
        current={currentPage}
        total={paginationTotal}
        defaultPageSize={meta.perPage}
        onChange={page => setCurrentPage(page)}
      />
    </Box>
  );

  return (
    <Flex w="100%" px={10} pt={5} pb={6} flexGrow={1} direction="column">
      <Flex>
        <PageTitle mb={6}>Tâches à effectuer</PageTitle>
      </Flex>
      <BaseTabs index={selectedPanelIndex} onChange={clearPageAndPush}>
        <TabList>
          <Tab>Toutes</Tab>

          {Object.entries(TASKS_TYPES).map(([type, label]) => (
            <TabWithBadge
              key={type}
              badgeBgColor={`tag.secondaryBg.${type}`}
              badgeContent={taskTypeCount(label)}
            >
              {label}
            </TabWithBadge>
          ))}
        </TabList>

        <TabPanels>
          <TabPanel>
            {selectedPanelIndex === 0 && (
              <AllTasksTab
                tasks={tasks}
                tags={tags}
                operators={operators}
                details={details}
                filters={filters}
                onFilterChange={updateFilter}
                pending={pending}
                onDeleteTask={fetchTasks}
                pagination={pagination}
              />
            )}
          </TabPanel>

          {Object.keys(TASKS_TYPES).map((type, index) => (
            <TabPanel key={type}>
              {selectedPanelIndex === index + 1 && (
                <FilteredTasksTab
                  tasks={tasks}
                  tags={tags}
                  operators={operators}
                  details={details}
                  filters={filters}
                  onFilterChange={updateFilter}
                  pending={pending}
                  onDeleteTask={fetchTasks}
                  pagination={pagination}
                />
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </BaseTabs>
    </Flex>
  );
};

const mapStateToProps = ({ tasks: { data: tasks, meta, pending } }) => ({ tasks, meta, pending });

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getTasks: tasksActions.getTasks,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(TasksTabs);
